/**
 * Lector de INSTANTES (createdAt y similares) en hora del centro de México.
 *
 * `moment(fecha)` / `dayjs(fecha)` formatean en la zona del navegador: quien abre el
 * ERP desde Cancún, Tijuana o fuera del país ve otra hora, y un registro hecho a las
 * 23:30 cae en otro día. Aquí la zona va fija, así que la pantalla, el Excel y el PDF
 * dicen lo mismo sin importar desde dónde se consulten.
 *
 * Usa `Intl` y no moment-timezone: éste sólo llega a node_modules como dependencia de
 * otra librería, no está declarado en package.json.
 *
 * No es para FECHAS-DÍA (caducidad, periodos...): ésas van con `fmtFechaDia`.
 */
export const ZONA_HORARIA_MEXICO = "America/Mexico_City";

// `hourCycle: "h23"` para que la medianoche salga como 00 y no como 24 (algunas
// versiones de Chrome lo hacían con `hour12: false`). El `% 24` de abajo cubre al resto.
const formateador = new Intl.DateTimeFormat("en-US", {
  timeZone: ZONA_HORARIA_MEXICO,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/** `{ anio, mes, dia, hora, minuto, segundo }` (mes de 1 a 12), o `null` si no hay fecha válida. */
export const partesFechaHoraMexico = (valor) => {
  if (!valor) return null;
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return null;
  const partes = {};
  for (const { type, value } of formateador.formatToParts(fecha)) partes[type] = value;
  return {
    anio: Number(partes.year),
    mes: Number(partes.month),
    dia: Number(partes.day),
    hora: Number(partes.hour) % 24,
    minuto: Number(partes.minute),
    segundo: Number(partes.second),
  };
};

const dosDigitos = (numero) => String(numero).padStart(2, "0");

/** "YYYY-MM-DD" del día en México; "" si no hay fecha. */
export const claveDiaMexico = (valor) => {
  const p = partesFechaHoraMexico(valor);
  return p ? `${p.anio}-${dosDigitos(p.mes)}-${dosDigitos(p.dia)}` : "";
};

/** "DD/MM/YYYY HH:mm" en México; "" si no hay fecha. */
export const fmtFechaHoraMexico = (valor) => {
  const p = partesFechaHoraMexico(valor);
  return p
    ? `${dosDigitos(p.dia)}/${dosDigitos(p.mes)}/${p.anio} ${dosDigitos(p.hora)}:${dosDigitos(p.minuto)}`
    : "";
};

/** "HH:mm" en México; "" si no hay fecha. */
export const fmtHoraMexico = (valor) => {
  const p = partesFechaHoraMexico(valor);
  return p ? `${dosDigitos(p.hora)}:${dosDigitos(p.minuto)}` : "";
};

/**
 * Número de serie de Excel (días desde el 30/12/1899, la hora como fracción) de la
 * hora de México. Se calcula aquí y no se le pasa un `Date` a la librería porque
 * ésta lo convierte con la zona del navegador, y además lo corre ~36 s (compara
 * contra el offset histórico de 1899 de Ciudad de México).
 */
export const serialExcelMexico = (valor) => {
  const p = partesFechaHoraMexico(valor);
  if (!p) return null;
  const local = Date.UTC(p.anio, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo);
  return (local - Date.UTC(1899, 11, 30)) / 86400000;
};
